import { IconChevronRight } from '@tabler/icons-react';
import { useT } from '@hana/i18n';
import { Skeleton, StateView, type ScreenState } from '@hana/ui';
import { CUS, JOB, MS, OB, SVCJ, TRF, delta, fi, sum, type Mode } from './data';
import s from './bodPhone.module.css';
import './i18n';

// Mint-BOD-Phone (390) · khung 1: 4 ô chỉ số · Job theo tháng · Sai sót & khen thưởng · Job chưa hoàn tất · KPI kỳ.
const EVS = [
  ['KPE-2610-001', 'bod.ph.e1', 'bod.ph.sConfirmed', 'pri'],
  ['KPE-2610-002', 'bod.ph.e2', 'bod.ph.sPending', 'warn'],
  ['KPE-2610-005', 'bod.ph.e3', 'bod.ph.sPending', 'warn'],
  ['KPE-2610-004', 'bod.ph.e4', 'bod.ph.sExplain', 'warn'],
] as const;
const STS = [['BK Standby', 180, 'cWarn'], ['BK Confirmed', 124, 'cPri'], ['BL Hold', 159, 'cPri'], ['BL Wait RLS', 37, 'cPri'], ['BL Released', 1251, 'cOk']] as const;
const SEG = [['Standby', 180, 'cWarn'], ['Active', 320, 'cPri'], ['Released', 1251, 'cOk']] as const;
const W = 360, H = 110, SW = W / 12, MX = 560;
const bar = (x: number, y: number, w: number, h: number) => (h > 0.2 ? `M${x.toFixed(1)} ${y.toFixed(1)}h${w.toFixed(1)}v${h.toFixed(1)}h${(-w).toFixed(1)}z ` : '');
const spark = (a: readonly number[]): string => {
  const mn = Math.min(...a), r = Math.max(...a) - mn || 1;
  return a.map((v, i) => `${i ? 'L' : 'M'}${(2 + (i * 116) / (a.length - 1)).toFixed(1)} ${(21 - ((v - mn) / r) * 18).toFixed(1)}`).join(' ');
};

export function BodPhone({ state, mode, idx, pidx, onKpi, onPick, onMode }: { state: ScreenState; mode: Mode; onMode: (m: Mode) => void; idx: number[]; pidx: number[]; onKpi: () => void; onPick: (code: string) => void }) {
  const t = useT();
  const tile = (k: string, a: readonly number[], sub?: string) => {
    const c = sum(a, idx), d = delta(c, sum(a, pidx));
    return { k, v: a === CUS ? fi(mode === 'y' ? sum(a, idx) / idx.length : a[11]!) : fi(c), sub: sub ?? d[0].replace(' so kỳ trước', t('bod.vsPrev')), good: sub ? null : d[1], a };
  };
  const tiles = [tile(t('bod.ph.jobs'), JOB), tile(t('bod.ph.cus'), CUS, mode === 'y' ? t('bod.ph.avgMonth') : t('bod.ph.cusSub')), tile(t('bod.ph.trf'), TRF), tile(t('bod.ph.svc'), SVCJ)];
  const bOb = OB.map((v, i) => { const h = (v / MX) * (H - 6); return bar(i * SW + SW * 0.2, H - h, SW * 0.6, h); }).join('');
  const bLs = JOB.map((v, i) => { const h1 = (OB[i]! / MX) * (H - 6), h = ((v - OB[i]!) / MX) * (H - 6); return bar(i * SW + SW * 0.2, H - h1 - h, SW * 0.6, h); }).join('');
  const hl = idx.map((i) => bar(i * SW + 1, 0, SW - 2, H)).join('');
  return (
    <div className={s.col}>
      <div className={s.per} role="radiogroup" aria-label={t('bod.modeLabel')}>
        {(['m', 'q', 'y'] as const).map((k) => <button key={k} type="button" role="radio" aria-checked={mode === k} onClick={() => onMode(k)}>{t(`bod.mode.${k}`)}</button>)}
      </div>
      <StateView state={state} skeleton={<Skeleton rows={5} />}>
        <div className={s.tiles}>
          {tiles.map((x) => (
            <div key={x.k} className={s.tile}>
              <span className={s.tk}>{x.k}</span>
              <b className={s.tv}>{x.v}</b>
              <span className={`${s.ts} ${x.good === true ? s.up : x.good === false ? s.down : ''}`}>{x.sub}</span>
              <svg role="img" aria-label={t('bod.spark', { k: x.k, a: MS[0]!, b: MS[11]! })} width="100%" height="24" viewBox="0 0 120 24" preserveAspectRatio="none"><path d={`${spark(x.a)} L118 24 L2 24 Z`} className={s.spA} /><path d={spark(x.a)} className={s.spL} vectorEffect="non-scaling-stroke" /></svg>
            </div>
          ))}
        </div>
        <section aria-label={t('bod.ph.chart')} className={s.card}>
          <div className={s.ph}><b>{t('bod.ph.chart')}</b><span>{t('bod.ph.12m')}</span></div>
          <svg role="img" aria-label={t('bod.chartAria', { v: MS.map((m, i) => `${m} ${fi(JOB[i]!)}`).join(', ') })} width="100%" height="110" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className={s.chart}>
            <path d={hl} className={s.hl} /><path d={bOb} className={s.fOb} /><path d={bLs} className={s.fLs} />
          </svg>
          <div className={s.xl}>{MS.map((k, i) => <span key={k} className={idx.includes(i) ? s.xon : ''}>{k}</span>)}</div>
          <div className={s.leg}><span><i className={s.fOb} />OB</span><span><i className={s.fLs} />LS · TR · IB</span></div>
        </section>
        <section aria-label={t('bod.ph.wait')} className={`${s.card} ${s.flush}`}>
          <div className={s.ph2}><b>{t('bod.ph.evs')}</b><span>{t('bod.ph.waitN', { n: 1 })}</span></div>
          {EVS.map(([code, d, st, kind]) => (
            <button key={code} type="button" className={s.ev} onClick={() => onPick(code)}>
              <span><b>{code}</b><span>{t(d)}</span></span>
              <em className={s[kind]}>{t(st)}</em>
            </button>
          ))}
        </section>
        <section aria-label={t('bod.ph.open')} className={s.card}>
          <b className={s.h}>{t('bod.ph.open')} {t('bod.ph.open120')}</b>
          <div className={s.segBar} role="img" aria-label={t('bod.openAria', { v: SEG.map((x) => `${x[0]} ${x[1]}`).join(', ') })}>{SEG.map(([k, n, c]) => <div key={k} className={s[c]} style={{ flex: `${n} 1 0` }} />)}</div>
          {STS.map(([k, n, c], i) => (
            <div key={k} className={s.row} style={{ borderTop: i ? undefined : '0' }}>
              <span className={s.rl}><i className={s[c]} /><span>{k === 'BL Released' ? `${k} · ${t('bod.notFinish')}` : k}</span></span>
              <span role="img" aria-label={`${k}: ${fi(n)}`} className={s.bt}><span className={s[c]} style={{ width: `${Math.max(2, (n / 1251) * 100).toFixed(1)}%` }} /></span>
              <b>{fi(n)}</b>
            </div>
          ))}
        </section>
        <button type="button" className={s.kpi} onClick={onKpi}>
          <span><b>{t('bod.ph.kpi')}</b><span>{t('bod.ph.kpiSub')}</span></span>
          <IconChevronRight size={18} aria-hidden="true" />
        </button>
      </StateView>
    </div>
  );
}
